import type { ReactNode } from 'react';
import { AuthProvider } from 'react-oidc-context';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { devAuthUser } from '@config/env';
import { oidcConfig } from '@core/auth/oidcConfig';
import { queryClient } from '@shared/lib/queryClient';
import { RealtimeProvider } from '@core/realtime/RealtimeProvider';
import { ThemeProvider, useTheme } from '@shared/theme';

export function AppProviders({ children }: { children: ReactNode }) {
  const inner = (
    <QueryClientProvider client={queryClient}>
      <RealtimeProvider>
        {children}
        <ThemedToaster />
      </RealtimeProvider>
    </QueryClientProvider>
  );

  // In dev-auth mode, skip the OIDC AuthProvider entirely so the SPA doesn't try to
  // initialise oidc-client-ts with missing Cognito metadata.
  const withAuth = devAuthUser ? inner : <AuthProvider {...oidcConfig}>{inner}</AuthProvider>;

  return <ThemeProvider>{withAuth}</ThemeProvider>;
}

/**
 * Sonner runs its own theme system that defaults to `'system'` (OS preference). Without this
 * override, toggling the app to light mode while the OS is dark (or vice versa) makes toasts
 * render in the wrong palette, breaking from the rest of the UI. Reads the app's `useTheme()`
 * so the toast palette tracks the toggle exactly.
 */
function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster position="top-right" richColors closeButton theme={theme} />;
}
